import { css } from '@emotion/react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/person.pb';
import { AdaptoActions } from '@frontend/adapto-actions';
import { generateTrackingId } from '@frontend/call-intel-core';
import {
  CallRecordActionName,
  CallRecordActionProps,
  DeleteCallRecordingModal,
  useDeleteCallRecording,
  useDownloadCallRecording,
  useCallRecordActionsGetter,
} from '@frontend/call-record-actions';
import { Icon } from '@frontend/icons';
import { Photos } from '@frontend/photos';
import { OnboardingHotspot, HOTSPOT_TARGET } from '@frontend/product-onboarding';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Text, useModalControl } from '@frontend/design-system';

type Contact = Required<Pick<Person, 'firstName' | 'lastName' | 'personId'>> & {
  profileName: string;
  number: string;
  defaultName: string;
  legalName: string;
};

type CallRecordActionsProps = {
  disableActions?: boolean;
  callRecord: HydratedCallRecord;
  hasDownloadAccess: boolean;
  hasDeleteAccess: boolean;
  onRecordingDeleted: () => void;
};

type CallInsightContactSectionProps = {
  contact: Contact;
  locationId: string;
  callDirection: string;
} & Omit<CallRecordActionsProps, 'callRecord'> & {
    callRecord: HydratedCallRecord | null;
  };

const PANEL_ACTION_PROPS: Partial<Record<CallRecordActionName, CallRecordActionProps>> = {
  call: { trackingIdSuffix: generateTrackingId({ prefix: 'call-insight-panel', context: 'call-contact' }) },
  message: {
    trackingIdSuffix: generateTrackingId({ prefix: 'call-insight-panel', context: 'message-contact' }),
    testId: 'message-contact',
  },
  viewProfile: { trackingId: generateTrackingId({ prefix: 'call-insight-panel', context: 'view-profile' }) },
  createContact: { trackingId: generateTrackingId({ prefix: 'call-insight-panel', context: 'create-contact' }) },
  downloadRecording: {
    trackingId: generateTrackingId({ prefix: 'call-insight-panel', context: 'download-recording' }),
  },
  deleteRecording: { trackingId: generateTrackingId({ prefix: 'call-insight-panel', context: 'delete-recording' }) },
};

const getPanelActionProps = (action: CallRecordActionName): CallRecordActionProps | undefined =>
  PANEL_ACTION_PROPS[action];

const CallRecordActions = ({
  disableActions = false,
  callRecord,
  hasDownloadAccess,
  hasDeleteAccess,
  onRecordingDeleted,
}: CallRecordActionsProps) => {
  const deleteModalControls = useModalControl();

  const { downloadMedia } = useDownloadCallRecording();
  const { mutate: deleteMedia } = useDeleteCallRecording({
    onSuccess: onRecordingDeleted,
  });

  const getCallRecordActions = useCallRecordActionsGetter({
    downloadMedia: (record) => {
      downloadMedia({ channelId: record.channelId, locationId: record.locationId });
    },
    deleteMedia: () => {
      deleteModalControls.openModal();
    },
    hasDownloadAccess,
    hasDeleteAccess,
    disableMessageAction: disableActions,
    getActionProps: getPanelActionProps,
  });

  const callRecordActions = getCallRecordActions({
    index: 0,
    rowData: callRecord,
  });

  return (
    <>
      <section css={styles.actionsSection}>
        <AdaptoActions
          enableCloseEvent
          iconColor='inherit'
          variant='icon-buttons'
          maxActions={3}
          popoverPlacement='left'
          moreActionsTrackingId={generateTrackingId({ prefix: 'call-insight-panel', context: 'more-actions' })}
          css={{ display: 'flex' }}
        >
          {callRecordActions}
        </AdaptoActions>
      </section>
      <DeleteCallRecordingModal
        {...deleteModalControls.modalProps}
        onConfirm={() => deleteMedia({ channelId: callRecord.channelId, locationId: callRecord.locationId })}
      />
    </>
  );
};

export const CallInsightContactSection = ({
  contact,
  locationId,
  callDirection,
  disableActions,
  callRecord,
  hasDownloadAccess,
  hasDeleteAccess,
  onRecordingDeleted,
}: CallInsightContactSectionProps) => {
  const { setPersonId } = useContactPanelShallowStore('setPersonId', 'personId');
  const { setShow } = useSlidePanelShallowStore('setShow');

  const { firstName, lastName, profileName, personId, number, defaultName, legalName } = contact;
  const isOutboundCall = callDirection === 'outbound';
  const isContactInteractive = !!personId && !disableActions;

  return (
    <div css={styles.wrapper}>
      <div css={styles.row}>
        <OnboardingHotspot
          target={HOTSPOT_TARGET.PATIENT_CONTACT_AVATAR}
          offset={{ top: 0, right: -4 }}
          enabled={isContactInteractive}
        >
          <Photos.ContactProfilePhoto
            css={styles.avatar}
            disableClick={!isContactInteractive}
            locationId={locationId}
            name={profileName || defaultName}
            personId={personId}
            title={legalName}
            data-testid='contact-profile-photo'
          />
        </OnboardingHotspot>

        <div css={styles.infoStack}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {firstName || lastName ? (
              <div
                css={[styles.name, !isContactInteractive && styles.nameDisabled]}
                onClick={() => {
                  if (isContactInteractive) {
                    setPersonId(personId, undefined, locationId);
                    setShow(true, 'contact');
                  }
                }}
                data-testid='contact-name'
              >
                {!!firstName && (
                  <Text as='span' weight='bold'>
                    {firstName}
                  </Text>
                )}
                {!!lastName && <Text as='span'> {lastName}</Text>}
              </div>
            ) : (
              <Text as='span' color='light'>
                {number}
              </Text>
            )}
            {isOutboundCall && <Icon name='phone-outgoing-small' css={{ marginLeft: theme.spacing(1) }} />}
          </div>

          {(firstName || lastName) && (
            <Text size='small' color='light'>
              {number}
            </Text>
          )}
        </div>
      </div>
      {!!callRecord && (
        <CallRecordActions
          callRecord={callRecord}
          disableActions={disableActions}
          hasDownloadAccess={hasDownloadAccess}
          hasDeleteAccess={hasDeleteAccess}
          onRecordingDeleted={onRecordingDeleted}
        />
      )}
    </div>
  );
};

const styles = {
  wrapper: css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  }),
  row: css({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  }),
  avatar: css({
    flexShrink: 0,
  }),
  infoStack: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  }),
  name: css({
    cursor: 'pointer',
    width: 'fit-content',
  }),
  nameDisabled: css({
    cursor: 'default',
  }),
  actionsSection: css`
    display: flex;
    justify-content: center;
    gap: ${theme.spacing(1)};
  `,
  adaptoActions: css({
    display: 'flex',
  }),
};
